import { useMemo, useState } from 'react';
import { Clock, AlertTriangle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { MasterTopbar } from '../shell/MasterTopbar';
import { useMasterBilling, type MasterBillingSubscription } from '../hooks/useMasterData';
import { fmtBRL } from '@/lib/utils';

type Tab = 'todas' | 'pendentes' | 'atrasadas';

function isOverdue(s: MasterBillingSubscription): boolean {
  if (!s.nextDueDate) return false;
  return new Date(s.nextDueDate).getTime() < Date.now() && s.status !== 'CANCELLED';
}

function statusVisuals(s: MasterBillingSubscription): { tone: string; label: string } {
  if (s.status === 'CANCELLED' || s.status === 'EXPIRED') return { tone: 'block', label: 'cancelada' };
  if (isOverdue(s)) return { tone: 'block', label: 'atrasada' };
  if (s.status === 'PENDING_PAYMENT') return { tone: 'warn', label: 'pendente' };
  if (s.status === 'TRIAL') return { tone: 'info', label: 'trial' };
  return { tone: 'pass', label: 'ativa' };
}

function formatBRLNoCents(n: number): string {
  return fmtBRL(n).replace(',00', '');
}

function formatShortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function MasterCobrancasScreen() {
  const { data, isLoading, error } = useMasterBilling();
  const [tab, setTab] = useState<Tab>('todas');

  const subs = data?.subscriptions ?? [];
  const mrr = data?.stats.mrr ?? 0;

  const stats = useMemo(() => {
    let pendentes = 0;
    let atrasadasValor = 0;
    let atrasadasCount = 0;
    for (const s of subs) {
      if (s.status === 'PENDING_PAYMENT' || s.status === 'TRIAL') pendentes += s.value;
      if (isOverdue(s)) {
        atrasadasValor += s.value;
        atrasadasCount += 1;
      }
    }
    return { pendentes, atrasadasValor, atrasadasCount };
  }, [subs]);

  const filtered = useMemo(() => {
    if (tab === 'todas') return subs;
    if (tab === 'pendentes') return subs.filter((s) => s.status === 'PENDING_PAYMENT' || s.status === 'TRIAL');
    return subs.filter(isOverdue);
  }, [subs, tab]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 18px 28px' }}>
      <MasterTopbar title="Cobranças" sub="Faturamento" />

      {/* Hero MRR */}
      <div
        style={{
          padding: '18px 20px',
          borderRadius: 18,
          background: 'linear-gradient(160deg, var(--mint-soft) 0%, var(--mint) 70%)',
          color: 'var(--on-mint)',
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontFamily: 'var(--font-mono)',
          }}
        >
          Receita recorrente · MRR
        </div>
        <div
          style={{
            fontSize: 34,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            marginTop: 8,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {formatBRLNoCents(mrr)}
        </div>
        <div style={{ fontSize: 12, opacity: 0.82, marginTop: 8, fontFamily: 'var(--font-mono)' }}>
          {data?.stats.active ?? 0} ativas · {data?.stats.pending ?? 0} pendentes ·{' '}
          {data?.stats.cancelled ?? 0} canceladas
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <KpiSmall
          icon={<Clock size={15} />}
          label="Em aberto"
          value={formatBRLNoCents(stats.pendentes)}
          hint={`${data?.stats.pending ?? 0} assinaturas`}
        />
        <KpiSmall
          icon={<AlertTriangle size={15} />}
          label="Atrasadas"
          value={formatBRLNoCents(stats.atrasadasValor)}
          hint={`${stats.atrasadasCount} ${stats.atrasadasCount === 1 ? 'assinatura' : 'assinaturas'}`}
          tone="ink"
        />
      </div>

      <button
        onClick={() => toast.info('Geração em lote estará no próximo release.')}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '11px 16px',
          background: 'var(--card-soft)',
          color: 'var(--fg-muted)',
          border: '1px dashed var(--card-stroke)',
          borderRadius: 12,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <Send size={14} /> Gerar cobranças do mês <span style={{ opacity: 0.7, fontSize: 10.5 }}>(em breve)</span>
      </button>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6 }}>
        {([['todas', 'Todas'], ['pendentes', 'Pendentes'], ['atrasadas', 'Atrasadas']] as const).map(
          ([id, label]) => {
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 600,
                  background: active ? 'var(--lilac-deep)' : 'var(--card-soft)',
                  color: active ? '#fff' : 'var(--fg-muted)',
                  border: active ? '1px solid transparent' : '1px solid var(--card-stroke)',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                {label}
              </button>
            );
          },
        )}
      </div>

      {/* Lista */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ height: 64, background: 'var(--card-soft)', borderRadius: 14 }} />
          ))}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: 14,
            background: 'var(--block-soft)',
            color: 'var(--block)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {(error as Error).message}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div style={{ padding: 16, color: 'var(--fg-dim)', fontSize: 13, textAlign: 'center' }}>
          Nenhuma assinatura neste filtro.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((s) => {
          const v = statusVisuals(s);
          return (
            <div
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 12px',
                background: 'var(--card)',
                border: '1px solid var(--card-stroke)',
                borderRadius: 14,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'var(--fg)',
                  }}
                >
                  {s.workspaceName ?? s.workspaceId.slice(0, 8)}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)',
                    color: isOverdue(s) ? 'var(--block)' : 'var(--fg-muted)',
                    marginTop: 2,
                  }}
                >
                  {s.plan} · {s.cycle === 'YEARLY' ? 'anual' : 'mensal'} · vence {formatShortDate(s.nextDueDate)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--fg)',
                  }}
                >
                  {formatBRLNoCents(s.value)}
                </div>
                <div style={{ marginTop: 4 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 10,
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      background: `var(--${v.tone}-soft)`,
                      color: `var(--${v.tone})`,
                    }}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: `var(--${v.tone})` }} />
                    {v.label}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface KpiSmallProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'ink';
}
function KpiSmall({ icon, label, value, hint, tone = 'default' }: KpiSmallProps) {
  const isInk = tone === 'ink';
  return (
    <div
      style={{
        padding: 12,
        background: isInk ? 'var(--ink-deep)' : 'var(--card)',
        border: isInk ? '1px solid rgba(255,255,255,0.05)' : '1px solid var(--card-stroke)',
        borderRadius: 14,
        color: isInk ? 'var(--on-ink)' : 'var(--fg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: 9.5,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
          }}
        >
          {label}
        </span>
        <span style={{ color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)' }}>{icon}</span>
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          marginTop: 6,
          fontFamily: 'var(--font-mono)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)',
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
